import {
  type FieldValues,
  type Path,
  type PathValue,
  useFormContext,
} from "react-hook-form";

import type { FormInput } from "../organisms/Form/Form.types";
import { SelectInput, SelectInputOption } from "./SelectInput";

export type SelectOptionType = { label: string; value: string };
type Props<
  T extends FieldValues,
  TValue extends PathValue<T, Path<T>> = PathValue<T, Path<T>>,
> = FormInput<T> & {
  placeholder?: string;
  hideLabel?: boolean;
  highlightBorder?: boolean;
  className?: string;
  options: SelectOptionType[];
  transformValue?: (value: string) => TValue;
  iconClassName?: string;
  contentClassName?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
};

export default function FormSelectInput<
  T extends FieldValues,
  TValue extends PathValue<T, Path<T>> = PathValue<T, Path<T>>,
>({
  name,
  label,
  hideLabel,
  placeholder,
  highlightBorder,
  className,
  options,
  transformValue,
  icon,
  iconClassName,
  contentClassName,
  disabled,
  readOnly,
  ...registerOptions
}: Props<T, TValue>) {
  const {
    register,
    setValue,
    watch,
    clearErrors,
    formState: { errors, defaultValues },
  } = useFormContext<T>();

  const defaultValue = defaultValues?.[name];

  const handleValueChange = (value: string) => {
    if (value !== null && value !== undefined) {
      clearErrors(name);
    }

    if (transformValue !== undefined) {
      setValue(name, transformValue(value), { shouldDirty: true });
      registerOptions.onChange?.(transformValue(value));
      return;
    }

    setValue(name, value as PathValue<T, Path<T>>, { shouldDirty: true });
    registerOptions.onChange?.(value);
  };

  const value = watch(name);
  const shouldHighlightBorder =
    !!highlightBorder && (value === undefined || value === "");

  return (
    <div className="relative">
      {!!label && !hideLabel && (
        <label className="mb-1 text-[13px] font-semibold screen850:text-sm">
          {label}
        </label>
      )}
      <SelectInput
        {...register(name, registerOptions)}
        defaultValue={
          defaultValue !== undefined ? String(defaultValue) : undefined
        }
        icon={icon}
        contentClassName={contentClassName}
        iconClassName={iconClassName}
        highlightBorder={shouldHighlightBorder}
        className={className}
        onSelect={handleValueChange}
        isErrored={!!errors[name]}
        errorMessage={errors[name]?.message?.toString()}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
      >
        {options.map(({ label, value }) => (
          <SelectInputOption key={value} value={value}>
            {label}
          </SelectInputOption>
        ))}
      </SelectInput>
    </div>
  );
}
